import { Tool, ContentNugget, PriorityPlaylist } from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';
import { selectIsSharedSessionActive } from '../sharedSession/sharedSession';
import type { RootState } from '../store';

type ContentByCompetencyResponse = Readonly<{
  nuggets: ContentNugget[];
  total: number;
  tools?: Tool[];
  playlists?: PriorityPlaylist[];
}>;

type ContentByCompetencyParams = Readonly<{
  competency_id: string;
  include?: string;
  sort?: string;
}>;

export const contentNuggetApi = createApi({
  reducerPath: 'contentNuggetApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getContentNuggetById: build.query<ContentNugget, string>({
      queryFn: async (id, queryApi, _extraOptions, baseQuery): Promise<{ data: ContentNugget }> => {
        const state = queryApi.getState() as RootState;
        const isSharedSession: boolean = selectIsSharedSessionActive(state);

        const url: string = isSharedSession
          ? `shared/solution-session/content-nugget/${id}`
          : `content-nugget/${id}`;

        return baseQuery({ url }) as Promise<{ data: ContentNugget }>;
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getContentByCompetency: build.query<ContentByCompetencyResponse, ContentByCompetencyParams>({
      query: ({ competency_id, include, sort }) => ({
        url: 'content-nugget/by-competency',
        params: {
          competency_id,
          ...(include && { include }),
          ...(sort && { sort }),
          filter: JSON.stringify({ exclude_theme: 'Grow by Doing' }),
        },
      }),
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getContentNuggetsByIds: build.query<ContentNugget[], number[]>({
      query: (ids) => ({
        url: 'content-nugget',
        params: {
          filter: JSON.stringify({ id: ids }),
          perPage: ids.length,
        },
      }),
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getContentNuggetsBySlugs: build.query<ContentNugget[], readonly string[]>({
      query: (slugs) => ({
        url: 'content-nugget/by-slugs',
        params: {
          slugs: slugs.join(','),
        },
      }),
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getContentNuggetsByCompetencySlugs: build.query<ContentNugget[], string[]>({
      query: (competencySlugs) => ({
        url: 'content-nugget/by-competency-slugs',
        params: {
          competency_slugs: competencySlugs.join(','),
          sort: 'value_rating',
          order: 'DESC',
          filter: JSON.stringify({ exclude_theme: 'Grow by Doing' }),
        },
      }),
      keepUnusedDataFor: 60 * 60 * 24, // Cache for 24 hours
    }),
  }),
});

export const {
  useGetContentNuggetByIdQuery,
  useGetContentByCompetencyQuery,
  useGetContentNuggetsByIdsQuery,
  useGetContentNuggetsBySlugsQuery,
  useGetContentNuggetsByCompetencySlugsQuery,
  usePrefetch: usePrefetchContentNugget,
  util: { resetApiState },
} = contentNuggetApi;
